import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { useVipStore } from "../context/VipContext";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

/* ------------------- AXIOS INSTANCE ------------------- */
const api = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
});

api.interceptors.request.use((config) => {
  const role = localStorage.getItem("role");
  const token = localStorage.getItem(`${role}Token`);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export default function VipAssignedGuards() {
  const { selectedVip } = useVipStore();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const vipId = selectedVip?.id;

  /* ---------------- RESPONSIVE ---------------- */
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isMobile = windowWidth <= 991;

  /* ---------------- FETCH DATA ---------------- */
  useEffect(() => {
    if (!vipId) {
      toast.error("No VIP Selected!");
      navigate("/vip-management");
      return;
    }
    fetchAssignedGuards();
  }, [vipId]);

  const fetchAssignedGuards = async () => {
    try {
      const res = await api.get(`/api/assignments/${vipId}`);
      if (res.data?.officer?.length > 0) {
        setResult(res.data);
      } else {
        toast.info("No Officer assigned yet!");
      }
    } catch (err) {
      toast.error("Failed to fetch assigned officer!");
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- SUMMARY ---------------- */
  const summary = useMemo(() => {
    if (!result?.officer) return [];
    const map = {};
    result.officer.forEach((o) => {
      map[o.rank] = (map[o.rank] || 0) + 1;
    });
    return Object.entries(map).map(([level, assigned]) => ({
      level,
      assigned,
    }));
  }, [result]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading Assigned Officers...</p>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="no-data-card">
        <h3>No Assigned Officers Found</h3>
      </div>
    );
  }

  return (
    <>
      <div className="assigned-guards-wrapper">
        {/* Header */}
        <header className="page-header">
          <div className="header-content">
            <div className="header-title">
              <h1>Assigned Officers</h1>
              <p>Current protection detail for {selectedVip?.name}</p>
            </div>
            <button onClick={() => navigate(-1)} className="back-btn">
              <i className="fas fa-arrow-left"></i>
              <span className="btn-text">Back</span>
            </button>
          </div>
        </header>

        {/* Main Content */}
        <main className="page-main">
          {/* Summary Card */}
          <section className="summary-card">
            <h2>Officer Summary by Rank</h2>
            <div className="summary-grid">
              {summary.map((s, i) => (
                <div key={i} className="summary-item">
                  <div className="summary-icon" style={{ backgroundColor: "#a855f7" }}>
                    <i className="fas fa-shield-alt"></i>
                  </div>
                  <div className="summary-info">
                    <h3 className="summary-value">{s.assigned}</h3>
                    <p className="summary-title">{s.level}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Details Section */}
          <section className="details-card">
            <h2>Officer Details</h2>

            {!isMobile ? (
              /* Desktop Table */
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Officer ID</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Rank</th>
                      <th>Experience</th>
                      <th>Status</th>
                      <th>Assigned From</th>
                      <th>Assigned Till</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.officer.map((o) => (
                      <tr key={o.id}>
                        <td>{o.id}</td>
                        <td>{o.name}</td>
                        <td>{o.email}</td>
                        <td>{o.rank}</td>
                        <td>{o.experience} yrs</td>
                        <td>{o.status}</td>
                        <td>{new Date(result.startAt).toLocaleString()}</td>
                        <td>{new Date(result.endAt).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              /* Mobile Cards */
              <div className="mobile-cards">
                {result.officer.map((o) => (
                  <div key={o.id} className="mobile-card">
                    <div className="mobile-header">
                      <strong>{o.name}</strong>
                      <span className="rank-badge">{o.rank}</span>
                    </div>
                    <div className="mobile-body">
                      <div className="mobile-row">
                        <span>ID:</span>
                        <strong>{o.id}</strong>
                      </div>
                      <div className="mobile-row">
                        <span>Email:</span>
                        <strong>{o.email}</strong>
                      </div>
                      <div className="mobile-row">
                        <span>Experience:</span>
                        <strong>{o.experience} yrs</strong>
                      </div>
                      <div className="mobile-row">
                        <span>Status:</span>
                        <strong>{o.status}</strong>
                      </div>
                      <div className="mobile-row">
                        <span>Assigned From:</span>
                        <strong>{new Date(result.startAt).toLocaleDateString()}</strong>
                      </div>
                      <div className="mobile-row">
                        <span>Assigned Till:</span>
                        <strong>{new Date(result.endAt).toLocaleDateString()}</strong>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </main>
      </div>

      {/* Light Purple Glassmorphic Theme + Responsive Enhancements */}
      <style jsx>{`
        .assigned-guards-wrapper {
          min-height: 100vh;
          background: linear-gradient(135deg, #e0d4ff 0%, #f0e6ff 100%);
          color: #4c1d95;
          padding: 0;
          width: 100%;
          overflow-x: hidden;
        }

        .page-header {
          background: rgba(255, 255, 255, 0.85);
          backdrop-filter: blur(12px);
          border-bottom: 1px solid rgba(168, 85, 247, 0.4);
          padding: 1.2rem 2rem;
          position: sticky;
          top: 0;
          z-index: 100;
        }

        .header-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
          max-width: 1400px;
          margin: 0 auto;
        }

        .header-title h1 {
          font-size: 1.8rem;
          font-weight: 700;
          color: #9333ea;
          margin: 0;
        }

        .header-title p {
          margin: 0.25rem 0 0;
          opacity: 0.8;
          font-size: 0.9rem;
          color: #6b46c1;
        }

        .back-btn {
          background: #a855f7;
          color: white;
          border: none;
          padding: 0.6rem 1.2rem;
          border-radius: 10px;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.3s ease;
        }

        .back-btn:hover {
          background: #9333ea;
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(168, 85, 247, 0.3);
        }

        .page-main {
          padding: 1.5rem 2rem;
          max-width: 1400px;
          margin: 0 auto;
        }

        @media (max-width: 768px) {
          .page-main { padding: 1rem; }
          .page-header { padding: 1rem 1.5rem; }
        }

        .summary-card,
        .details-card,
        .no-data-card {
          background: rgba(255, 255, 255, 0.75);
          backdrop-filter: blur(12px);
          border-radius: 16px;
          padding: 1.8rem;
          margin-bottom: 1.5rem;
          border: 1px solid rgba(168, 85, 247, 0.5);
          transition: all 0.3s ease;
        }

        .summary-card:hover,
        .details-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 10px 25px rgba(168, 85, 247, 0.25);
        }

        .summary-card h2,
        .details-card h2 {
          font-size: 1.5rem;
          font-weight: 700;
          margin-bottom: 1.2rem;
          color: #6b21a8;
        }

        .summary-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 1.2rem;
        }

        .summary-item {
          display: flex;
          align-items: center;
          gap: 1.2rem;
          background: rgba(255, 255, 255, 0.8);
          padding: 1.2rem;
          border-radius: 14px;
          border: 1px solid rgba(168, 85, 247, 0.3);
        }

        .summary-icon {
          width: 60px;
          height: 60px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 1.6rem;
          flex-shrink: 0;
        }

        .summary-value {
          font-size: 2rem;
          font-weight: 800;
          margin: 0;
          color: #581c87;
        }

        .summary-title {
          opacity: 0.9;
          margin-top: 0.3rem;
          font-size: 1rem;
          font-weight: 500;
          color: #7c3aed;
        }

        .table-container {
          overflow-x: auto;
          border-radius: 12px;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
        }

        .data-table {
          width: 100%;
          min-width: 900px;
          border-collapse: collapse;
          background: white;
        }

        .data-table thead {
          background: #a855f7;
          color: white;
        }

        .data-table th,
        .data-table td {
          padding: 1rem 1.2rem;
          text-align: left;
          white-space: nowrap;
        }

        .data-table th {
          font-weight: 600;
          font-size: 0.95rem;
        }

        .data-table tbody tr {
          transition: background 0.2s ease;
        }

        .data-table tbody tr:hover {
          background: rgba(168, 85, 247, 0.1);
        }

        .data-table tbody td {
          border-bottom: 1px solid rgba(168, 85, 247, 0.2);
          color: #4c1d95;
        }

        .mobile-cards {
          display: flex;
          flex-direction: column;
          gap: 1.2rem;
        }

        .mobile-card {
          background: rgba(255, 255, 255, 0.9);
          border-radius: 14px;
          padding: 1.4rem;
          border-left: 5px solid #a855f7;
          box-shadow: 0 6px 20px rgba(168, 85, 247, 0.15);
        }

        .mobile-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
          padding-bottom: 0.8rem;
          border-bottom: 1px solid rgba(168, 85, 247, 0.3);
        }

        .mobile-header strong {
          font-size: 1.2rem;
          color: #581c87;
        }

        .rank-badge {
          background: #a855f7;
          color: white;
          padding: 0.4rem 0.8rem;
          border-radius: 20px;
          font-size: 0.85rem;
          font-weight: 600;
        }

        .mobile-body {
          display: flex;
          flex-direction: column;
          gap: 0.8rem;
        }

        .mobile-row {
          display: flex;
          justify-content: space-between;
          font-size: 0.95rem;
        }

        .mobile-row span {
          opacity: 0.8;
          color: #6b46c1;
        }

        .mobile-row strong {
          color: #4c1d95;
          font-weight: 600;
        }

        .loading-container {
          text-align: center;
          padding: 4rem;
          color: #9333ea;
        }

        .spinner {
          width: 50px;
          height: 50px;
          border: 4px solid #e9d5ff;
          border-top: 4px solid #a855f7;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 1rem;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .no-data-card {
          text-align: center;
          padding: 4rem;
          background: rgba(255, 255, 255, 0.75);
          backdrop-filter: blur(12px);
          border-radius: 16px;
          margin: 2rem;
          border: 1px solid rgba(168, 85, 247, 0.5);
        }

        .no-data-card h3 {
          color: #9333ea;
          font-size: 1.5rem;
        }
      `}</style>
    </>
  );
}








// import React, { useEffect, useState, useMemo } from "react";
// import axios from "axios";
// import { useVipStore } from "../context/VipContext";
// import { useNavigate } from "react-router-dom";
// import { toast } from "react-toastify";

// /* ------------------- AXIOS INSTANCE ------------------- */
// const api = axios.create({
//   baseURL: process.env.REACT_APP_BASE_URL,
// });

// api.interceptors.request.use((config) => {
//   const role = localStorage.getItem("role");
//   const token = localStorage.getItem(`${role}Token`);
//   if (token) config.headers.Authorization = `Bearer ${token}`;
//   return config;
// });

// export default function VipAssignedGuards() {
//   const { selectedVip } = useVipStore();
//   const navigate = useNavigate();

//   const [loading, setLoading] = useState(true);
//   const [result, setResult] = useState(null);
//   const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

//   const vipId = selectedVip?.id;

//   /* ---------------- RESPONSIVE ---------------- */
//   useEffect(() => {
//     const resize = () => setIsMobile(window.innerWidth < 768);
//     window.addEventListener("resize", resize);
//     return () => window.removeEventListener("resize", resize);
//   }, []);

//   /* ---------------- FETCH DATA ---------------- */
//   useEffect(() => {
//     if (!vipId) {
//       toast.error("No VIP Selected!");
//       navigate("/vip-management");
//       return;
//     }
//     fetchAssignedGuards();
//   }, [vipId]);

//   const fetchAssignedGuards = async () => {
//     try {
//       const res = await api.get(`/api/assignments/${vipId}`);
//       if (res.data?.officer?.length > 0) {
//         setResult(res.data);
//       } else {
//         toast.info("No Officer assigned yet!");
//       }
//     } catch (err) {
//       toast.error("Failed to fetch assigned officer!");
//     } finally {
//       setLoading(false);
//     }
//   };

//   /* ---------------- SUMMARY ---------------- */
//   const summary = useMemo(() => {
//     if (!result?.officer) return [];
//     const map = {};
//     result.officer.forEach((o) => {
//       map[o.rank] = (map[o.rank] || 0) + 1;
//     });
//     return Object.entries(map).map(([level, assigned]) => ({
//       level,
//       assigned,
//     }));
//   }, [result]);

//   if (loading) return <h3 style={{ padding: 30 }}>Loading Assigned Officer...</h3>;
//   if (!result) return <h3 style={{ padding: 30 }}>No Assigned Officer Found</h3>;

//   return (
//     <div style={styles.container}>
//       <h2 style={styles.title}>Assigned Officer for {selectedVip?.name}</h2>

//       {/* ================= SUMMARY ================= */}
//       <div style={styles.card}>
//         <h3>Officer Summary</h3>

//         <div style={styles.tableWrapper}>
//           <table style={styles.table}>
//             <thead>
//               <tr>
//                 <th style={styles.tableHeader}>Rank</th>
//                 <th style={styles.tableHeader}>Assigned</th>
//               </tr>
//             </thead>
//             <tbody>
//               {summary.map((s, i) => (
//                 <tr key={i}>
//                   <td style={styles.tableCell}>{s.level}</td>
//                   <td style={styles.tableCell}>{s.assigned}</td>
//                 </tr>
//               ))}
//             </tbody>
//           </table>
//         </div>
//       </div>

//       {/* ================= DETAILS ================= */}
//       <div style={styles.responseBox}>
//         <h3>Officers Details</h3>

//         {/* ===== DESKTOP / TABLET ===== */}
//         {!isMobile ? (
//           <div style={styles.tableWrapper}>
//             <table style={styles.table}>
//               <thead>
//                 <tr>
//                   <th style={styles.tableHeader}>Officer ID</th>
//                   <th style={styles.tableHeader}>Name</th>
//                   <th style={styles.tableHeader}>Email</th>
//                   <th style={styles.tableHeader}>Rank</th>
//                   <th style={styles.tableHeader}>Experience</th>
//                   <th style={styles.tableHeader}>Status</th>
//                   <th style={styles.tableHeader}>Assigned At</th>
//                   <th style={styles.tableHeader}>Assigned Till</th>
//                 </tr>
//               </thead>
//               <tbody>
//                 {result.officer.map((o) => (
//                   <tr key={o.id}>
//                     <td style={styles.tableCell}>{o.id}</td>
//                     <td style={styles.tableCell}>{o.name}</td>
//                     <td style={styles.tableCell}>{o.email}</td>
//                     <td style={styles.tableCell}>{o.rank}</td>
//                     <td style={styles.tableCell}>{o.experience} yrs</td>
//                     <td style={styles.tableCell}>{o.status}</td>
//                     <td style={styles.tableCell}>
//                       {new Date(result.startAt).toLocaleString()}
//                     </td>
//                     <td style={styles.tableCell}>
//                       {new Date(result.endAt).toLocaleString()}
//                     </td>
//                   </tr>
//                 ))}
//               </tbody>
//             </table>
//           </div>
//         ) : (
//           /* ===== MOBILE CARDS ===== */
//           <div style={styles.mobileList}>
//             {result.officer.map((o) => (
//               <div key={o.id} style={styles.mobileCard}>
//                 <div style={styles.mobileRow}><span>ID</span><b>{o.id}</b></div>
//                 <div style={styles.mobileRow}><span>Name</span><b>{o.name}</b></div>
//                 <div style={styles.mobileRow}><span>Email</span><b>{o.email}</b></div>
//                 <div style={styles.mobileRow}><span>Rank</span><b>{o.rank}</b></div>
//                 <div style={styles.mobileRow}><span>Experience</span><b>{o.experience} yrs</b></div>
//                 <div style={styles.mobileRow}><span>Status</span><b>{o.status}</b></div>
//                 <div style={styles.mobileRow}>
//                   <span>Assigned</span>
//                   <b>{new Date(result.startAt).toLocaleString()}</b>
//                 </div>
//                 <div style={styles.mobileRow}>
//                   <span>Till</span>
//                   <b>{new Date(result.endAt).toLocaleString()}</b>
//                 </div>
//               </div>
//             ))}
//           </div>
//         )}

//         <button
//           style={{ ...styles.btn, marginTop: 20 }}
//           onClick={() => navigate(-1)}
//         >
//           ⬅ Back
//         </button>
//       </div>
//     </div>
//   );
// }

// /* ---------------- STYLES ---------------- */
// const styles = {
//   container: { padding: 30 },

//   title: {
//     fontSize: 28,
//     fontWeight: "bold",
//     marginBottom: 20,
//     color: "#1967d2",
//   },

//   card: {
//     background: "#fff",
//     padding: 20,
//     borderRadius: 12,
//     marginBottom: 20,
//   },

//   responseBox: {
//     background: "rgba(255,255,255,0.55)",
//     padding: 25,
//     borderRadius: 14,
//   },

//   btn: {
//     background: "#1967d2",
//     color: "#fff",
//     border: "none",
//     padding: "12px 20px",
//     borderRadius: 10,
//     cursor: "pointer",
//     fontSize: 16,
//   },

//   /* ===== TABLE RESPONSIVE ===== */
//   tableWrapper: {
//     width: "100%",
//     overflowX: "auto",
//     WebkitOverflowScrolling: "touch",
//   },

//   table: {
//     width: "100%",
//     minWidth: "900px",
//     marginTop: 15,
//     borderCollapse: "collapse",
//   },

//   tableHeader: {
//     background: "#1967d2",
//     color: "#fff",
//     padding: "10px 12px",
//     textAlign: "left",
//     whiteSpace: "nowrap",
//     position: "sticky",
//     top: 0,
//   },

//   tableCell: {
//     padding: "10px 12px",
//     borderBottom: "1px solid #ddd",
//     whiteSpace: "nowrap",
//     fontSize: 14,
//   },

//   /* ===== MOBILE ===== */
//   mobileList: {
//     display: "flex",
//     flexDirection: "column",
//     gap: 15,
//     marginTop: 15,
//   },

//   mobileCard: {
//     background: "#fff",
//     padding: 15,
//     borderRadius: 12,
//     borderLeft: "4px solid #1967d2",
//   },

//   mobileRow: {
//     display: "flex",
//     justifyContent: "space-between",
//     padding: "6px 0",
//     fontSize: 14,
//   },
// };



